/**
 * Conteúdo do blog.
 *
 * ## Por que aqui e não no dicionário
 *
 * `src/i18n` guarda a moldura da interface, e o verificador exige que toda
 * chave exista nos três idiomas. Para um post isso seria a regra errada: um
 * texto pode legitimamente sair só em português primeiro, e travar a
 * publicação até a tradução ficar pronta empurraria alguém a publicar tradução
 * ruim. Aqui a falta de um idioma cai no inglês, como no resto do produto, mas
 * ninguém é obrigado a preencher os três de saída.
 *
 * ## Por que não um CMS
 *
 * O modelo em `Propostas Páginas` sugeria trocar isto por uma chamada a CMS.
 * Não há CMS, e montar um para publicar um punhado de notas técnicas custaria
 * mais do que o problema que resolve. Quando a frequência justificar, o formato
 * abaixo é o contrato que a tela sabe renderizar — trocar a origem não muda a
 * página.
 *
 * ## O que não entra
 *
 * O modelo vinha com seis posts de exemplo assinados por um autor fictício e
 * citando medições que nunca fizemos — "em 40 domínios rastreados neste mês,
 * 71% dos títulos duplicados vinham de três templates". É marcação de
 * rascunho, não conteúdo: publicar aquilo seria inventar pesquisa de mercado
 * na página pública do produto. Ficou o desenho; o texto é escrito do zero, e
 * cada número precisa sair de algo que dá para conferir.
 *
 * ## Formato do corpo
 *
 * Uma lista de blocos, em vez de HTML ou markdown: sem parser, sem
 * `dangerouslySetInnerHTML`, e o conjunto de blocos é pequeno de propósito —
 * `h2`, `p`, `lista` e `nota`. Um post que precise de mais que isso
 * provavelmente quer ser outra coisa. A exceção é `calculadora`, a conta da
 * nota para o leitor mexer; só `NewBlogPost` a desenha.
 *
 * ## `capa.figura`
 *
 * A figura real do post nas páginas novas: `'nota'` mostra os cinco pesos na
 * lista e a tela de uma execução na abertura do post. Sem ela, o título faz o
 * papel de imagem. `CapaDoPost`, do blog antigo, ignora o campo.
 *
 * ## `seo`
 *
 * Title e description da busca, quando `titulo` e `resumo` não cabem nas
 * faixas que o próprio produto cobra (`LIMIARES` em `utils/seoMetrics`):
 * 30–60 caracteres com a marca, 120–160 na description. O build confere — ver
 * `scripts/gerarSitemap.js`. Sem `seo`, valem `titulo` e `resumo`.
 *
 * ## `slug` e `slugs`
 *
 * `slug` é o endereço em português e a identidade do post; `slugs` traz o de
 * inglês e espanhol, para a URL falar a língua da página. Sem tradução, vale
 * `slug`. Qualquer slug do post abre o post: a página leva ao do idioma da URL,
 * e é assim que os endereços de antes de `slugs` existir continuam de pé.
 */

export const CATEGORIAS = {
  produto: { chave: 'blog.catProduto', tom: 'sp-tag-neutral' },
  metodo: { chave: 'blog.catMetodo', tom: 'sp-tag-accent' },
  youtube: { chave: 'blog.catYoutube', tom: 'sp-tag-crit' }
};

export const POSTS = [
  {
    slug: 'favicon-sumiu-do-google-redirecionamento',
    slugs: { en: 'favicon-missing-from-google-redirect', es: 'favicon-desaparecio-de-google-redireccion' },
    categoria: 'metodo',
    data: '2026-11-02',
    leitura: 5,
    capa: {
      rotulo: {
        en: 'curl -sIL',
        pt: 'curl -sIL',
        es: 'curl -sIL'
      },
      tom: 'var(--sp-accent)',
      semente: 23,
      barras: 10
    },
    seo: {
      titulo: {
        en: 'Favicon in Google: redirects and the rules',
        pt: 'Favicon no Google: redirecionamento e regras',
        es: 'Favicon en Google: redirecciones y reglas'
      },
      descricao: {
        en: 'Google now says Googlebot must follow every redirect from the home page to the favicon. How to check it on a client site, plus the other rules that hide it.',
        pt: 'O Google agora exige que o Googlebot siga os redirecionamentos da home até o favicon. Como conferir no site do cliente e as outras regras que somem com o ícone.',
        es: 'Google ahora exige que Googlebot siga las redirecciones de la home hasta el favicon. Cómo revisarlo en el sitio del cliente y qué otras reglas lo ocultan.'
      }
    },
    titulo: {
      en: 'Favicon missing from Google? Start with the home page redirect',
      pt: 'O favicon sumiu do Google? Comece pelo redirecionamento da home',
      es: '¿El favicon desapareció de Google? Empieza por la redirección de la home'
    },
    resumo: {
      en: 'In October 2026 Google clarified that Googlebot has to follow every redirect from the home page to the page that declares the favicon. A broken step is enough for the gray globe to show up. Plus the formats and the one-icon-per-hostname rule.',
      pt: 'Em outubro de 2026 o Google deixou claro que o Googlebot precisa seguir cada redirecionamento da home até a página que declara o favicon. Um passo quebrado basta para aparecer o globo cinza. E mais: os formatos aceitos e a regra de um ícone por hostname.',
      es: 'En octubre de 2026 Google aclaró que Googlebot tiene que seguir cada redirección de la home hasta la página que declara el favicon. Basta un paso roto para que aparezca el globo gris. Además: los formatos aceptados y la regla de un ícono por hostname.'
    },
    corpo: {
      pt: [
        {
          tipo: 'p',
          texto:
            'Em 27 de outubro de 2026, o Google reescreveu uma linha da documentação de favicon. Não basta mais o Googlebot rastrear a home: ele precisa conseguir seguir cada redirecionamento que sai dela até a página onde o ícone está declarado, e nenhuma URL desse caminho pode estar bloqueada. Se a home do seu cliente manda o visitante para outro endereço antes de mostrar qualquer coisa, é ali que o globo cinza no lugar do logo costuma nascer. O Google chamou a mudança de esclarecimento, e ela nem entrou no registro de atualizações da documentação.'
        },
        { tipo: 'h2', texto: 'Por que o redirecionamento da home decide o favicon' },
        {
          tipo: 'p',
          texto:
            'Porque o Google procura o ícone num lugar só: a home do hostname. O robô abre a raiz, vai para onde ela mandar e lê a tag link no cabeçalho da página onde chegou. Se um elo dessa corrente falha, a tag nunca é lida, e o resultado mostra o ícone genérico.'
        },
        {
          tipo: 'p',
          texto: 'Alguns desenhos de site quebram essa corrente com frequência:'
        },
        {
          tipo: 'lista',
          itens: [
            'Home que escolhe o idioma pelo navegador, por cookie ou pela localização e redireciona para /pt-br/ ou /en/. O caminho que o robô percorre pode não ser o mesmo do visitante.',
            'Home que leva primeiro para uma tela de login, de confirmação de idade ou de consentimento.',
            'Pasta de destino, ou a pasta onde mora o arquivo do ícone, bloqueada no robots.txt. Acontece com /assets/ e /static/ em sites que fecharam essas pastas anos atrás e esqueceram.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Nenhum desses casos é raro em site de agência. O primeiro, em especial, aparece em quase toda loja que vende para mais de um país.'
        },
        { tipo: 'h2', texto: 'As outras regras que tiram o ícone do resultado' },
        {
          tipo: 'p',
          texto:
            'O redirecionamento é a novidade, mas está longe de ser a única condição. Pela documentação do Google, conferida em 2 de novembro de 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'O arquivo precisa ser quadrado, com pelo menos 8x8 pixels. A recomendação é passar de 48x48, para o ícone ficar nítido em telas diferentes.',
            'Os formatos listados são BMP, GIF, ICO, PNG, JPEG, PPM e TIFF.',
            'O Googlebot-Image tem de rastrear o arquivo; o Googlebot, a home. O arquivo pode ficar numa CDN, fora do domínio.',
            'A tag vai no cabeçalho da home, com rel igual a icon, shortcut icon, apple-touch-icon ou apple-touch-icon-precomposed.',
            'O endereço do arquivo deve ficar estável. Trocar o nome a cada deploy atrapalha.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Repare no que falta naquela lista de formatos: SVG. Muito site novo declara só um ícone vetorial, que fica nítido em qualquer tamanho e funciona bem no navegador. Na Busca, pela lista publicada, esse arquivo não tem garantia de ser aceito. Uma segunda tag apontando para um PNG quadrado de 48x48 ou maior resolve, e o SVG pode continuar onde está.'
        },
        { tipo: 'h2', texto: 'Um favicon por hostname: o que isso muda em site com idiomas' },
        {
          tipo: 'p',
          texto:
            'O Google aceita um ícone por hostname. Subdomínio conta como hostname à parte, então loja.cliente.com.br pode ter o seu. Subpasta, não: cliente.com.br/es/ herda o da home. Quem quer ícone diferente por país ou por marca precisa de subdomínio.'
        },
        {
          tipo: 'p',
          texto:
            'Pela mesma regra, www.cliente.com.br e cliente.com.br são hostnames diferentes. Quando a versão sem www redireciona para a com www, a corrente que o Google precisa seguir começa um passo antes, e o redirecionamento de protocolo, de http para https, também entra na conta. Cada salto é um lugar a mais onde uma regra de firewall, um robots.txt antigo ou uma tela intermediária podem barrar o robô.'
        },
        {
          tipo: 'p',
          texto:
            'A regra conversa com o redirecionamento. Num site que manda a raiz para uma pasta de idioma, vale a tag da página onde a corrente termina para o robô. E o próprio Google diz que os endereços IP do Googlebot parecem estar nos Estados Unidos. Se cada versão de idioma declara um arquivo diferente, o ícone do resultado tende a ser o da versão que um visitante americano receberia.'
        },
        { tipo: 'h2', texto: 'Quanto tempo o ícone corrigido leva para aparecer?' },
        {
          tipo: 'p',
          texto:
            'De alguns dias a algumas semanas, segundo o Google, porque a home precisa ser rastreada e processada de novo. Pedir a indexação da home pela Inspeção de URL do Search Console adianta a fila. Mesmo com tudo em ordem, o Google não promete exibir o favicon: cumprir as regras é condição, não garantia.'
        },
        {
          tipo: 'p',
          texto:
            'Na prática, isso muda a conversa com o cliente. Ícone trocado na sexta ainda não tem motivo para estar no resultado na segunda, e cobrar antes disso só gera chamado.'
        },
        { tipo: 'h2', texto: 'O que conferir na segunda-feira' },
        {
          tipo: 'lista',
          itens: [
            'Rode curl -sIL https://dominio-do-cliente/ no terminal e anote cada 301, 302, 307 ou 308 até o 200 final. A última URL é onde a tag do favicon precisa estar.',
            'Abra o código-fonte dessa página com Ctrl+U, e não pelo inspetor, e procure rel="icon". O Google não documenta se aceita tag inserida por JavaScript; com a tag no HTML servido, a dúvida some.',
            'Teste a home e cada URL do caminho na Inspeção de URL. Depois, confira no relatório de robots.txt do Search Console se a pasta do arquivo está liberada.',
            'Veja formato e tamanho: se o site só tem SVG, acrescente um PNG quadrado de 48x48 ou maior.',
            'Se mudou alguma coisa, peça a indexação da home e anote a data. Volte a olhar o resultado depois de algumas semanas.'
          ]
        },
        {
          tipo: 'nota',
          texto:
            'Um site com favicon certo hoje pode perdê-lo amanhã por causa de um redirecionamento novo de idioma ou de consentimento. Vale repetir o curl sempre que a home mudar de comportamento.'
        }
      ],
      en: [
        {
          tipo: 'p',
          texto:
            'On October 27, 2026, Google rewrote one line of its favicon documentation. Crawling the home page is no longer enough: Googlebot has to get through every redirect that leaves it, all the way to whichever page declares the icon, and no URL along that path can be blocked. If your client\'s home page sends visitors somewhere else before showing anything, that is usually where the gray globe replacing the logo comes from. Google called it a clarification, and it never made the documentation changelog.'
        },
        { tipo: 'h2', texto: 'Why the home page redirect decides the favicon' },
        {
          tipo: 'p',
          texto:
            'Because Google looks for the icon in one place only: the home page of the hostname. The crawler opens the root, goes wherever it is sent and reads the link tag in the header of the page where it lands. If any link in that chain breaks, the tag is never read and the result shows the generic icon.'
        },
        {
          tipo: 'p',
          texto: 'A few site setups break the chain often:'
        },
        {
          tipo: 'lista',
          itens: [
            'A home page that picks the language by browser, cookie or location and redirects to /en/ or /pt-br/. The path the crawler takes may not match the visitor\'s.',
            'A home page that sends people to a login, age check or consent screen first.',
            'A destination folder, or the folder holding the icon file, blocked in robots.txt. It happens with /assets/ and /static/ on sites that closed those folders years ago and forgot.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'None of these is rare on agency client sites. The first one, in particular, shows up on almost every store that sells to more than one country.'
        },
        { tipo: 'h2', texto: 'The other rules that keep the icon out of results' },
        {
          tipo: 'p',
          texto:
            'The redirect is the news, but it is far from the only condition. According to Google\'s documentation, checked on November 2, 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'The file must be square, at least 8x8 pixels. Google recommends going above 48x48 so the icon stays sharp across screens.',
            'The listed formats are BMP, GIF, ICO, PNG, JPEG, PPM and TIFF.',
            'Googlebot-Image needs access to the file, and Googlebot to the home page. The file can live on a CDN, off your domain.',
            'The tag goes in the home page header, with rel set to icon, shortcut icon, apple-touch-icon or apple-touch-icon-precomposed.',
            'The file URL should stay stable. Renaming it on every deploy gets in the way.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Notice what is missing from that format list: SVG. Plenty of new sites declare only a vector icon, which looks sharp at any size and works fine in the browser. In Search, going by the published list, that file is not guaranteed to be accepted. A second tag pointing to a square PNG of 48x48 or larger fixes it, and the SVG can stay where it is.'
        },
        { tipo: 'h2', texto: 'One favicon per hostname: what it means for multilingual sites' },
        {
          tipo: 'p',
          texto:
            'Google accepts one icon per hostname. A subdomain counts as its own hostname, so shop.client.com can have its own. A subfolder cannot: client.com/es/ inherits the home page icon. If you want a different icon per country or brand, you need a subdomain.'
        },
        {
          tipo: 'p',
          texto:
            'By the same rule, www.client.com and client.com are different hostnames. When the bare domain redirects to the www version, the chain Google has to follow starts one step earlier, and the http to https redirect counts too. Every hop is one more place where a firewall rule, an old robots.txt or an interstitial screen can stop the crawler.'
        },
        {
          tipo: 'p',
          texto:
            'This ties back to the redirect. On a site that sends the root to a language folder, the tag that counts is the one on the page where the chain ends for the crawler. Google itself says Googlebot seems to crawl from US-based IPs. If each language version declares a different file, the icon in results will likely be the one an American visitor would get.'
        },
        { tipo: 'h2', texto: 'How long does a fixed icon take to show up?' },
        {
          tipo: 'p',
          texto:
            'Anywhere from a few days to a few weeks, according to Google, because the home page has to be crawled and processed again. Requesting indexing of the home page in Search Console\'s URL Inspection tool moves it up the queue. Even with everything right, Google does not promise to show the favicon: following the rules is a requirement, not a guarantee.'
        },
        {
          tipo: 'p',
          texto:
            'In practice, that changes the conversation with the client. An icon swapped on Friday has no reason to be in results by Monday, and chasing it sooner just creates tickets.'
        },
        { tipo: 'h2', texto: 'What to check on Monday' },
        {
          tipo: 'lista',
          itens: [
            'Run curl -sIL https://client-domain/ in a terminal and write down every 301, 302, 307 or 308 until the final 200. The last URL is where the favicon tag has to be.',
            'Open that page\'s source with Ctrl+U, not the inspector, and search for rel="icon". Google does not document whether it accepts a tag injected by JavaScript; with the tag in the served HTML, the question goes away.',
            'Test the home page and every URL on the path in URL Inspection. Then check Search Console\'s robots.txt report to make sure the icon\'s folder is allowed.',
            'Check format and size: if the site only has an SVG, add a square PNG of 48x48 or larger.',
            'If you changed anything, request indexing of the home page and note the date. Look at the results again after a few weeks.'
          ]
        },
        {
          tipo: 'nota',
          texto:
            'A site with a working favicon today can lose it tomorrow because of a new language or consent redirect. Rerun the curl whenever the home page changes behavior.'
        }
      ],
      es: [
        {
          tipo: 'p',
          texto:
            'El 27 de octubre de 2026, Google reescribió una línea de su documentación sobre favicons. Ya no alcanza con que Googlebot rastree la home: tiene que poder seguir cada redirección que sale de ella hasta la página donde se declara el ícono, y ninguna URL de ese camino puede estar bloqueada. Si la home de tu cliente manda al visitante a otra dirección antes de mostrar algo, ahí suele nacer el globo gris que reemplaza al logo. Google lo llamó una aclaración, y ni siquiera entró en el registro de cambios de la documentación.'
        },
        { tipo: 'h2', texto: 'Por qué la redirección de la home decide el favicon' },
        {
          tipo: 'p',
          texto:
            'Porque Google busca el ícono en un solo lugar: la home del hostname. El robot abre la raíz, va adonde lo manden y lee la etiqueta link en el encabezado de la página donde termina. Si un eslabón de esa cadena falla, la etiqueta nunca se lee y el resultado muestra el ícono genérico.'
        },
        {
          tipo: 'p',
          texto: 'Algunos diseños de sitio rompen esa cadena con frecuencia:'
        },
        {
          tipo: 'lista',
          itens: [
            'Una home que elige el idioma por navegador, cookie o ubicación y redirige a /es/ o /en/. El camino que recorre el robot puede no ser el mismo que el del visitante.',
            'Una home que lleva primero a una pantalla de login, de verificación de edad o de consentimiento.',
            'La carpeta de destino, o la carpeta donde vive el archivo del ícono, bloqueada en robots.txt. Pasa con /assets/ y /static/ en sitios que cerraron esas carpetas hace años y se olvidaron.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Ninguno de estos casos es raro en sitios de agencia. El primero, sobre todo, aparece en casi toda tienda que vende a más de un país.'
        },
        { tipo: 'h2', texto: 'Las otras reglas que sacan el ícono del resultado' },
        {
          tipo: 'p',
          texto:
            'La redirección es la novedad, pero está lejos de ser la única condición. Según la documentación de Google, revisada el 2 de noviembre de 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'El archivo tiene que ser cuadrado, de al menos 8x8 píxeles. Google recomienda superar 48x48 para que el ícono se vea nítido en distintas pantallas.',
            'Los formatos listados son BMP, GIF, ICO, PNG, JPEG, PPM y TIFF.',
            'Googlebot-Image tiene que poder rastrear el archivo, y Googlebot la home. El archivo puede estar en una CDN, fuera del dominio.',
            'La etiqueta va en el encabezado de la home, con rel igual a icon, shortcut icon, apple-touch-icon o apple-touch-icon-precomposed.',
            'La URL del archivo debería mantenerse estable. Cambiarle el nombre en cada deploy estorba.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Fíjate en lo que falta en esa lista de formatos: SVG. Muchos sitios nuevos declaran solo un ícono vectorial, que se ve nítido en cualquier tamaño y funciona bien en el navegador. En la Búsqueda, según la lista publicada, ese archivo no tiene garantía de ser aceptado. Una segunda etiqueta que apunte a un PNG cuadrado de 48x48 o más lo resuelve, y el SVG puede quedarse donde está.'
        },
        { tipo: 'h2', texto: 'Un favicon por hostname: qué cambia en un sitio con idiomas' },
        {
          tipo: 'p',
          texto:
            'Google acepta un ícono por hostname. Un subdominio cuenta como hostname aparte, así que tienda.cliente.com puede tener el suyo. Una subcarpeta no: cliente.com/es/ hereda el de la home. Si quieres un ícono distinto por país o por marca, necesitas un subdominio.'
        },
        {
          tipo: 'p',
          texto:
            'Por la misma regla, www.cliente.com y cliente.com son hostnames distintos. Cuando la versión sin www redirige a la que lleva www, la cadena que Google tiene que seguir empieza un paso antes, y la redirección de http a https también suma. Cada salto es un lugar más donde una regla de firewall, un robots.txt viejo o una pantalla intermedia pueden frenar al robot.'
        },
        {
          tipo: 'p',
          texto:
            'Esto se cruza con la redirección. En un sitio que manda la raíz a una carpeta de idioma, vale la etiqueta de la página donde termina la cadena para el robot. Y el propio Google dice que las direcciones IP de Googlebot parecen estar en Estados Unidos. Si cada versión de idioma declara un archivo distinto, el ícono del resultado tiende a ser el de la versión que recibiría un visitante estadounidense.'
        },
        { tipo: 'h2', texto: '¿Cuánto tarda en aparecer el ícono corregido?' },
        {
          tipo: 'p',
          texto:
            'Entre unos días y unas semanas, según Google, porque la home tiene que rastrearse y procesarse de nuevo. Pedir la indexación de la home en la Inspección de URLs de Search Console adelanta la fila. Aun con todo en orden, Google no promete mostrar el favicon: cumplir las reglas es condición, no garantía.'
        },
        {
          tipo: 'p',
          texto:
            'En la práctica, eso cambia la conversación con el cliente. Un ícono cambiado el viernes no tiene por qué estar en el resultado el lunes, y reclamarlo antes solo genera tickets.'
        },
        { tipo: 'h2', texto: 'Qué revisar el lunes' },
        {
          tipo: 'lista',
          itens: [
            'Ejecuta curl -sIL https://dominio-del-cliente/ en la terminal y anota cada 301, 302, 307 o 308 hasta el 200 final. La última URL es donde tiene que estar la etiqueta del favicon.',
            'Abre el código fuente de esa página con Ctrl+U, no con el inspector, y busca rel="icon". Google no documenta si acepta una etiqueta insertada por JavaScript; con la etiqueta en el HTML servido, la duda desaparece.',
            'Prueba la home y cada URL del camino en la Inspección de URLs. Después, revisa en el informe de robots.txt de Search Console que la carpeta del archivo esté permitida.',
            'Mira formato y tamaño: si el sitio solo tiene SVG, agrega un PNG cuadrado de 48x48 o más.',
            'Si cambiaste algo, pide la indexación de la home y anota la fecha. Vuelve a mirar el resultado después de algunas semanas.'
          ]
        },
        {
          tipo: 'nota',
          texto:
            'Un sitio con el favicon bien hoy puede perderlo mañana por una nueva redirección de idioma o de consentimiento. Conviene repetir el curl cada vez que la home cambie de comportamiento.'
        }
      ]
    }
  },
  {
    slug: 'robots-txt-sinal-comum-contra-treino-de-ia',
    slugs: { en: 'robots-txt-line-to-refuse-ai-training', es: 'robots-txt-linea-comun-contra-entrenar-ia' },
    categoria: 'metodo',
    data: '2026-10-30',
    leitura: 6,
    capa: {
      rotulo: {
        en: '__usage_ai-training',
        pt: '__usage_ai-training',
        es: '__usage_ai-training'
      },
      tom: 'var(--sp-accent)',
      semente: 47,
      barras: 12
    },
    seo: {
      titulo: {
        en: 'One robots.txt line to refuse AI training',
        pt: 'Robots.txt: a linha comum contra treino de IA',
        es: 'Robots.txt: la línea común contra entrenar IA'
      },
      descricao: {
        en: 'Google, Microsoft, OpenAI and Anthropic will honor one robots.txt group to refuse AI training. What it changes, when, and what it leaves untouched.',
        pt: 'Google, Microsoft, OpenAI e Anthropic vão respeitar um só grupo no robots.txt para recusar treino de IA. O que muda, quando, e o que fica como está.',
        es: 'Google, Microsoft, OpenAI y Anthropic respetarán un solo grupo en robots.txt para rechazar el entrenamiento de IA. Qué cambia, cuándo y qué no.'
      }
    },
    titulo: {
      en: 'robots.txt now has one line to refuse AI training. It removes nobody from search',
      pt: 'O robots.txt ganhou uma linha para recusar treino de IA. Ela não tira ninguém da busca',
      es: 'robots.txt ganó una línea para rechazar el entrenamiento de IA. No saca a nadie de la búsqueda'
    },
    resumo: {
      en: 'Four AI companies agreed on a single robots.txt group that says "do not train on this". It covers training only, none of them fully applies it yet, and on Google it can take Gemini citations along with it.',
      pt: 'Quatro empresas de IA combinaram um único grupo no robots.txt que diz "não treine com isto". Ele vale só para treino, nenhuma o aplica por completo ainda e, no Google, pode levar junto as citações no Gemini.',
      es: 'Cuatro empresas de IA acordaron un único grupo en robots.txt que dice "no entrenes con esto". Vale solo para entrenamiento, ninguna lo aplica del todo todavía y, en Google, puede arrastrar las citas en Gemini.'
    },
    corpo: {
      pt: [
        {
          tipo: 'p',
          texto:
            'Em 30 de outubro de 2026, Google, Microsoft, OpenAI e Anthropic disseram que vão respeitar um mesmo grupo no robots.txt, User-agent: __usage_ai-training, como pedido para que o conteúdo do site não seja usado no treino de modelos de IA. A linha trata de treino e de mais nada. Ela não tira o site da Busca do Google nem do Bing, não é ordem para robô algum deixar de rastrear e, por enquanto, nenhuma das quatro empresas a aplica por completo.'
        },
        { tipo: 'h2', texto: 'O que a linha nova faz, e o que ela deixa de fora' },
        {
          tipo: 'p',
          texto:
            'Ela troca várias regras por uma. Até aqui, quem não queria ver o próprio conteúdo em treino precisava nomear cada empresa: GPTBot para a OpenAI, ClaudeBot para a Anthropic, Google-Extended para o Google. Com o token comum, a preferência é escrita uma vez só.'
        },
        {
          tipo: 'nota',
          texto:
            'Exemplo: User-agent: __usage_ai-training na primeira linha e Disallow: / na segunda recusam treino no site inteiro. Para recusar só uma pasta, troque a barra pelo caminho dela, como Disallow: /artigos/.'
        },
        {
          tipo: 'p',
          texto:
            'O rascunho que define o token chama de treino o uso do conteúdo para alterar os parâmetros de um modelo generativo. Modelos que só classificam, ordenam ou dão nota ficam de fora. E o próprio texto avisa que o token registra uma preferência, não uma ordem de rastreio: o robô pode continuar baixando as páginas, contanto que não as use para treinar.'
        },
        { tipo: 'h2', texto: 'Quando cada empresa passa a respeitar o token?' },
        {
          tipo: 'p',
          texto: 'Cada uma tem a sua data. Pelo que publicaram em 30 de outubro de 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'Anthropic: diz que já começou a implementar e que vai levar algum tempo até cobrir todos os seus serviços.',
            'Microsoft: o Bing começa a ler as preferências novas em 1º de fevereiro de 2027, à medida que rastreia as páginas.',
            'Google: pretende dar suporte em 2027, sem mês definido, e prometeu publicar o andamento.',
            'OpenAI: segundo a imprensa especializada, promete implementar nos próximos meses e divulgar os detalhes quando isso acontecer.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'O documento também pede cuidado. Trata-se de um rascunho individual no IETF, assinado por Kevin Kelley, da Anthropic, e discutido no grupo de trabalho sobre preferências de IA. Não tem status de padrão. Se ninguém o renovar, expira em 3 de maio de 2027.'
        },
        { tipo: 'h2', texto: 'Por que o asterisco não conta como recusa' },
        {
          tipo: 'p',
          texto:
            'Um grupo User-agent: * não vale como o token novo, e o rascunho diz isso com todas as letras. O robots.txt que já bloqueia tudo para todo mundo continua barrando o rastreio, mas não registra preferência alguma sobre treino pelo canal comum. Para que ela exista, o nome __usage_ai-training precisa estar escrito no arquivo.'
        },
        {
          tipo: 'p',
          texto:
            'No sentido contrário, o engano é parecido. Pôr o token no arquivo não impede o GPTBot nem o ClaudeBot de rastrear. As regras por nome de robô seguem valendo como antes, ao lado da nova.'
        },
        { tipo: 'h2', texto: 'Treinar e responder continuam sendo robôs diferentes' },
        {
          tipo: 'p',
          texto:
            'Confundir o robô que coleta para treinar com o que busca para responder é o erro mais caro desse assunto, e a linha nova só fala do primeiro. Bloquear quem treina é uma decisão sobre o uso do conteúdo. Já o bloqueio de quem responde tira o site justamente de onde o cliente quer ser citado.'
        },
        {
          tipo: 'lista',
          itens: [
            'OpenAI: o GPTBot coleta para treino; o OAI-SearchBot decide o que entra nas respostas de busca do ChatGPT. A própria OpenAI diz que uma mudança no robots.txt leva cerca de 24 horas para aparecer na busca.',
            'Anthropic: o ClaudeBot é o de treino, o Claude-SearchBot indexa para busca e o Claude-User abre a página quando alguém pede no chat.',
            'Google: o Google-Extended não é um robô, é um nome que só existe no robots.txt. A documentação do Google afirma que ele não interfere na inclusão nem no ranking da Busca.'
          ]
        },
        { tipo: 'h2', texto: 'No Google, o atalho pode levar o Gemini junto' },
        {
          tipo: 'p',
          texto:
            'O Google explicou como vai interpretar o token: se o site recusar treino pela linha comum e não disser nada sobre o Google-Extended, conta como se tivesse bloqueado o Google-Extended. Acontece que, pela documentação atual, esse mesmo nome também controla o uso do conteúdo para fundamentar respostas no app Gemini e na Vertex AI. Quem recusa treino pelo atalho pode sumir, de quebra, das respostas do Gemini que citam fontes.'
        },
        {
          tipo: 'p',
          texto:
            'O anúncio lembra que sites que liberam o Google-Extended de forma explícita seguem aparecendo nessas respostas, porque a regra escrita por nome vence a interpretação. O preço é que liberar esse nome libera também o treino do Gemini: hoje as duas coisas andam juntas. Nossa leitura é que essa escolha precisa ser feita por escrito com o cliente, e não herdada de um atalho. O Google prometeu atualizações ao longo de 2027, então é uma regra que vamos reler.'
        },
        { tipo: 'h2', texto: 'No Bing, a separação chega à página' },
        {
          tipo: 'p',
          texto:
            'A Microsoft aproveitou o anúncio para separar treino de grounding, que é o uso do conteúdo para montar uma resposta com citação. Treino aceita o token no robots.txt ou uma marcação por página, em meta tag ou no cabeçalho X-Robots-Tag. Grounding só se configura por página, com três opções: liberar, recusar ou liberar apenas para produtos da própria Microsoft, como o Copilot.'
        },
        {
          tipo: 'p',
          texto:
            'Há dois prazos para quem usa NoArchive ou NoCache com a intenção de manter o cliente fora da IA da Microsoft. A partir de 15 de dezembro de 2026, o Bing Webmaster Tools aceita uma preferência temporária de grounding. Depois de 1º de março de 2027, essas duas tags deixam de contar como recusa de treino ou de grounding. Segundo a Microsoft, nada disso mexe na posição dentro da busca tradicional do Bing.'
        },
        { tipo: 'h2', texto: 'Como medir isso nos clientes' },
        {
          tipo: 'p',
          texto:
            'No Silent Puma, a seção Robôs de IA de cada execução mostra o que o robots.txt diz para GPTBot, OAI-SearchBot, ClaudeBot, PerplexityBot, Google-Extended e outros, com quem lê para treinar separado de quem lê para responder. Ela mostra, não julga: bloquear de propósito é escolha do cliente. O token novo ainda não está nessa lista, então, por ora, a conferência dele é abrindo o arquivo.'
        },
        { tipo: 'h2', texto: 'O que fazer na segunda-feira' },
        {
          tipo: 'lista',
          itens: [
            'Abra o /robots.txt de cada cliente e anote, por nome, quais robôs de IA estão bloqueados e em qual grupo.',
            'Marque ao lado de cada um se ele lê para treinar ou para responder. Um bloqueio de OAI-SearchBot ou de Claude-SearchBot herdado de template é o primeiro a investigar.',
            'Faça ao cliente as duas perguntas separadas: ele aceita treino? Quer aparecer nas respostas com citação?',
            'Se ele recusar treino, acrescente o grupo __usage_ai-training e mantenha as regras por nome. Até cada empresa confirmar o suporte, são elas que funcionam.',
            'Escreva a regra do Google-Extended de propósito, pensando no que o cliente quer no Gemini.',
            'Para quem usa NoArchive ou NoCache por causa do Bing, marque 1º de março de 2027 na agenda.',
            'Depois de mexer na regra de um robô de busca da OpenAI, espere um dia antes de conferir o efeito.'
          ]
        }
      ],
      en: [
        {
          tipo: 'p',
          texto:
            'On October 30, 2026, Google, Microsoft, OpenAI and Anthropic said they will honor the same robots.txt group, User-agent: __usage_ai-training, as a request not to use a site’s content to train AI models. The line is about training and nothing else. It does not remove the site from Google or Bing search, it is not an order for any bot to stop crawling, and for now none of the four companies fully applies it.'
        },
        { tipo: 'h2', texto: 'What the new line does, and what it leaves out' },
        {
          tipo: 'p',
          texto:
            'It replaces several rules with one. Until now, anyone who wanted their content kept out of training had to name each company: GPTBot for OpenAI, ClaudeBot for Anthropic, Google-Extended for Google. With the shared token, you write the preference once.'
        },
        {
          tipo: 'nota',
          texto:
            'Example: User-agent: __usage_ai-training on the first line and Disallow: / on the second refuse training for the whole site. To refuse a single folder, replace the slash with its path, as in Disallow: /articles/.'
        },
        {
          tipo: 'p',
          texto:
            'The draft that defines the token calls training any use of content to change the parameters of a generative model. Models that only classify, rank or score are excluded. The text also makes clear that the token records a preference, not a crawl instruction: a bot may keep fetching pages as long as it does not train on them.'
        },
        { tipo: 'h2', texto: 'When does each company start honoring the token?' },
        {
          tipo: 'p',
          texto: 'Each one has its own date. Going by what they published on October 30, 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'Anthropic: says it has already started implementing it and that covering all of its services will take some time.',
            'Microsoft: Bing starts reading the new preferences on February 1, 2027, as it crawls pages.',
            'Google: plans to support it in 2027, with no month set, and promised progress updates.',
            'OpenAI: according to trade press, it intends to implement it in the coming months and share details when it does.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'The document itself deserves caution. It is an individual IETF draft, written by Kevin Kelley of Anthropic and discussed in the AI preferences working group. It is not a standard. If nobody renews it, it expires on May 3, 2027.'
        },
        { tipo: 'h2', texto: 'Why the asterisk does not count as a refusal' },
        {
          tipo: 'p',
          texto:
            'A User-agent: * group does not stand in for the new token, and the draft says so explicitly. A robots.txt that already blocks everything for everyone still stops crawling, but it records no training preference through the shared channel. For that preference to exist, the name __usage_ai-training has to be written in the file.'
        },
        {
          tipo: 'p',
          texto:
            'The opposite mistake is just as easy. Adding the token does not stop GPTBot or ClaudeBot from crawling. Rules by bot name keep working exactly as before, next to the new one.'
        },
        { tipo: 'h2', texto: 'Training and answering are still different bots' },
        {
          tipo: 'p',
          texto:
            'Mixing up the bot that collects for training with the one that fetches for answers is the costliest error here, and the new line only covers the first. Blocking the trainer is a decision about how content is used. Shutting out the answerer pulls the site out of exactly the place where the client wants to be cited.'
        },
        {
          tipo: 'lista',
          itens: [
            'OpenAI: GPTBot collects for training; OAI-SearchBot decides what goes into ChatGPT search answers. OpenAI says a robots.txt change takes about 24 hours to show up in search.',
            'Anthropic: ClaudeBot is the training bot, Claude-SearchBot indexes for search, and Claude-User opens a page when someone asks for it in chat.',
            'Google: Google-Extended is not a bot but a name that only exists in robots.txt. Google’s documentation states it has no effect on inclusion or ranking in Search.'
          ]
        },
        { tipo: 'h2', texto: 'On Google, the shortcut can take Gemini with it' },
        {
          tipo: 'p',
          texto:
            'Google explained how it will read the token: if a site refuses training through the shared line and says nothing about Google-Extended, Google treats it as if Google-Extended were blocked. Under the current documentation, though, that same name also controls whether content grounds answers in the Gemini app and Vertex AI. Refusing training through the shortcut can quietly remove a site from Gemini answers that cite sources.'
        },
        {
          tipo: 'p',
          texto:
            'The announcement notes that sites explicitly allowing Google-Extended keep appearing in those answers, because a rule written by name beats the interpretation. The catch is that allowing that name also allows Gemini training: today the two go together. Our view is that this choice should be made in writing with the client, not inherited from a shortcut. Google promised updates through 2027, so this is a rule we will revisit.'
        },
        { tipo: 'h2', texto: 'On Bing, the split goes down to the page' },
        {
          tipo: 'p',
          texto:
            'Microsoft used the announcement to separate training from grounding, meaning the use of content to build an answer with a citation. Training accepts the token in robots.txt or a page-level setting in a meta tag or the X-Robots-Tag header. Grounding can only be set per page, with three options: allow, refuse, or allow only Microsoft’s own products, such as Copilot.'
        },
        {
          tipo: 'p',
          texto:
            'Two deadlines matter if you use NoArchive or NoCache to keep a client out of Microsoft’s AI. From December 15, 2026, Bing Webmaster Tools accepts a temporary grounding preference. After March 1, 2027, those two tags no longer count as a training or grounding refusal. According to Microsoft, none of this affects rankings in traditional Bing search.'
        },
        { tipo: 'h2', texto: 'How to measure this across clients' },
        {
          tipo: 'p',
          texto:
            'In Silent Puma, the AI bots section of each run shows what robots.txt says to GPTBot, OAI-SearchBot, ClaudeBot, PerplexityBot, Google-Extended and others, with training bots kept apart from answering bots. It shows, it does not judge: blocking on purpose is the client’s call. The new token is not on that list yet, so for now you check it by opening the file.'
        },
        { tipo: 'h2', texto: 'What to do on Monday' },
        {
          tipo: 'lista',
          itens: [
            'Open each client’s /robots.txt and write down, by name, which AI bots are blocked and in which group.',
            'Next to each one, note whether it reads to train or to answer. An OAI-SearchBot or Claude-SearchBot block inherited from a template is the first to investigate.',
            'Ask the client two separate questions: do they accept training? Do they want to appear in answers with citations?',
            'If they refuse training, add the __usage_ai-training group and keep the rules by name. Until each company confirms support, those are the ones that work.',
            'Write the Google-Extended rule on purpose, based on what the client wants in Gemini.',
            'For anyone using NoArchive or NoCache because of Bing, put March 1, 2027 on the calendar.',
            'After changing a rule for an OpenAI search bot, wait a day before checking the effect.'
          ]
        }
      ],
      es: [
        {
          tipo: 'p',
          texto:
            'El 30 de octubre de 2026, Google, Microsoft, OpenAI y Anthropic dijeron que respetarán un mismo grupo en robots.txt, User-agent: __usage_ai-training, como pedido para que el contenido del sitio no se use para entrenar modelos de IA. La línea habla de entrenamiento y de nada más. No saca al sitio de la búsqueda de Google ni de Bing, no es una orden para que ningún robot deje de rastrear y, por ahora, ninguna de las cuatro empresas la aplica por completo.'
        },
        { tipo: 'h2', texto: 'Qué hace la línea nueva y qué deja afuera' },
        {
          tipo: 'p',
          texto:
            'Cambia varias reglas por una. Hasta ahora, quien no quería ver su contenido en un entrenamiento tenía que nombrar a cada empresa: GPTBot para OpenAI, ClaudeBot para Anthropic, Google-Extended para Google. Con el token común, la preferencia se escribe una sola vez.'
        },
        {
          tipo: 'nota',
          texto:
            'Ejemplo: User-agent: __usage_ai-training en la primera línea y Disallow: / en la segunda rechazan el entrenamiento en todo el sitio. Para rechazarlo solo en una carpeta, cambie la barra por su ruta, como Disallow: /articulos/.'
        },
        {
          tipo: 'p',
          texto:
            'El borrador que define el token llama entrenamiento al uso del contenido para modificar los parámetros de un modelo generativo. Los modelos que solo clasifican, ordenan o puntúan quedan afuera. El texto aclara además que el token expresa una preferencia, no una orden de rastreo: el robot puede seguir descargando páginas siempre que no entrene con ellas.'
        },
        { tipo: 'h2', texto: '¿Cuándo empieza cada empresa a respetar el token?' },
        {
          tipo: 'p',
          texto: 'Cada una tiene su fecha. Según lo que publicaron el 30 de octubre de 2026:'
        },
        {
          tipo: 'lista',
          itens: [
            'Anthropic: dice que ya empezó a implementarlo y que llevará un tiempo cubrir todos sus servicios.',
            'Microsoft: Bing empieza a leer las preferencias nuevas el 1 de febrero de 2027, a medida que rastrea las páginas.',
            'Google: piensa darle soporte en 2027, sin mes definido, y prometió publicar los avances.',
            'OpenAI: según la prensa especializada, planea implementarlo en los próximos meses y dar los detalles cuando lo haga.'
          ]
        },
        {
          tipo: 'p',
          texto:
            'El documento también pide cautela. Es un borrador individual en el IETF, firmado por Kevin Kelley, de Anthropic, y discutido en el grupo de trabajo sobre preferencias de IA. No es un estándar. Si nadie lo renueva, vence el 3 de mayo de 2027.'
        },
        { tipo: 'h2', texto: 'Por qué el asterisco no cuenta como rechazo' },
        {
          tipo: 'p',
          texto:
            'Un grupo User-agent: * no equivale al token nuevo, y el borrador lo dice de forma expresa. El robots.txt que ya bloquea todo para todos sigue frenando el rastreo, pero no registra ninguna preferencia sobre entrenamiento por el canal común. Para que exista, el nombre __usage_ai-training tiene que estar escrito en el archivo.'
        },
        {
          tipo: 'p',
          texto:
            'Al revés, la confusión es parecida. Poner el token en el archivo no impide que GPTBot o ClaudeBot rastreen. Las reglas por nombre de robot siguen funcionando igual que antes, junto a la nueva.'
        },
        { tipo: 'h2', texto: 'Entrenar y responder siguen siendo robots distintos' },
        {
          tipo: 'p',
          texto:
            'Confundir el robot que recolecta para entrenar con el que busca para responder es el error más caro de este tema, y la línea nueva solo habla del primero. Bloquear al que entrena es una decisión sobre el uso del contenido. En cambio, bloquear al que responde saca al sitio justo de donde el cliente quiere ser citado.'
        },
        {
          tipo: 'lista',
          itens: [
            'OpenAI: GPTBot recolecta para entrenamiento; OAI-SearchBot decide qué entra en las respuestas de búsqueda de ChatGPT. La propia OpenAI dice que un cambio en robots.txt tarda unas 24 horas en verse en la búsqueda.',
            'Anthropic: ClaudeBot es el de entrenamiento, Claude-SearchBot indexa para búsqueda y Claude-User abre la página cuando alguien la pide en el chat.',
            'Google: Google-Extended no es un robot, es un nombre que solo existe en robots.txt. La documentación de Google afirma que no influye en la inclusión ni en el ranking de la Búsqueda.'
          ]
        },
        { tipo: 'h2', texto: 'En Google, el atajo puede llevarse a Gemini' },
        {
          tipo: 'p',
          texto:
            'Google explicó cómo va a leer el token: si el sitio rechaza el entrenamiento con la línea común y no dice nada sobre Google-Extended, cuenta como si hubiera bloqueado Google-Extended. Pero, según la documentación actual, ese mismo nombre también controla el uso del contenido para fundamentar respuestas en la app de Gemini y en Vertex AI. Quien rechaza el entrenamiento por el atajo puede desaparecer, de paso, de las respuestas de Gemini que citan fuentes.'
        },
        {
          tipo: 'p',
          texto:
            'El anuncio recuerda que los sitios que habilitan Google-Extended de forma explícita siguen apareciendo en esas respuestas, porque la regla escrita por nombre gana sobre la interpretación. El costo es que habilitar ese nombre también habilita el entrenamiento de Gemini: hoy las dos cosas van juntas. Nuestra lectura es que esta decisión se toma por escrito con el cliente, no se hereda de un atajo. Google prometió novedades durante 2027, así que es una regla que vamos a volver a leer.'
        },
        { tipo: 'h2', texto: 'En Bing, la separación llega a la página' },
        {
          tipo: 'p',
          texto:
            'Microsoft aprovechó el anuncio para separar entrenamiento de grounding, que es el uso del contenido para armar una respuesta con cita. El entrenamiento acepta el token en robots.txt o una marca por página, en meta tag o en la cabecera X-Robots-Tag. El grounding solo se configura por página, con tres opciones: permitir, rechazar o permitir solo los productos de la propia Microsoft, como Copilot.'
        },
        {
          tipo: 'p',
          texto:
            'Hay dos plazos para quien usa NoArchive o NoCache con la idea de dejar al cliente fuera de la IA de Microsoft. Desde el 15 de diciembre de 2026, Bing Webmaster Tools acepta una preferencia temporal de grounding. Después del 1 de marzo de 2027, esas dos etiquetas dejan de contar como rechazo de entrenamiento o de grounding. Según Microsoft, nada de esto cambia la posición en la búsqueda tradicional de Bing.'
        },
        { tipo: 'h2', texto: 'Cómo medirlo en los clientes' },
        {
          tipo: 'p',
          texto:
            'En Silent Puma, la sección Robots de IA de cada ejecución muestra lo que robots.txt les dice a GPTBot, OAI-SearchBot, ClaudeBot, PerplexityBot, Google-Extended y otros, con los que leen para entrenar separados de los que leen para responder. Muestra, no juzga: bloquear a propósito es decisión del cliente. El token nuevo todavía no está en esa lista, así que por ahora se revisa abriendo el archivo.'
        },
        { tipo: 'h2', texto: 'Qué hacer el lunes' },
        {
          tipo: 'lista',
          itens: [
            'Abra el /robots.txt de cada cliente y anote, por nombre, qué robots de IA están bloqueados y en qué grupo.',
            'Al lado de cada uno, marque si lee para entrenar o para responder. Un bloqueo de OAI-SearchBot o de Claude-SearchBot heredado de una plantilla es el primero que hay que investigar.',
            'Hágale al cliente dos preguntas separadas: ¿acepta el entrenamiento? ¿Quiere aparecer en respuestas con cita?',
            'Si rechaza el entrenamiento, agregue el grupo __usage_ai-training y mantenga las reglas por nombre. Hasta que cada empresa confirme el soporte, esas son las que funcionan.',
            'Escriba la regla de Google-Extended a propósito, pensando en lo que el cliente quiere en Gemini.',
            'Para quien usa NoArchive o NoCache por Bing, anote el 1 de marzo de 2027 en la agenda.',
            'Después de cambiar la regla de un robot de búsqueda de OpenAI, espere un día antes de revisar el efecto.'
          ]
        }
      ]
    }
  },
  {
    slug: 'como-o-score-de-seo-e-calculado',
    slugs: { en: 'how-the-seo-score-is-calculated', es: 'como-se-calcula-el-score-de-seo' },
    categoria: 'metodo',
    data: '2026-08-24',
    leitura: 6,
    capa: {
      /* O rótulo é texto na tela como qualquer outro: sem os três idiomas ele
         saía em português no meio de uma página em inglês. */
      rotulo: {
        en: '100 − Σ(weight × affected)',
        pt: '100 − Σ(peso × afetadas)',
        es: '100 − Σ(peso × afectadas)'
      },
      tom: 'var(--sp-accent)',
      semente: 31,
      barras: 11,
      figura: 'nota'
    },
    seo: {
      titulo: {
        en: 'How the SEO score is calculated',
        pt: 'Como o score de SEO é calculado',
        es: 'Cómo se calcula el score de SEO'
      },
      descricao: {
        en: 'The Silent Puma SEO score in five weights and one subtraction, why a page counts once per area, and why a run with no pages gets no score.',
        pt: 'O score de SEO do Silent Puma em cinco pesos e uma subtração, por que uma página conta uma vez por área e por que execução sem páginas fica sem score.',
        es: 'El score de SEO de Silent Puma en cinco pesos y una resta, por qué una página cuenta una vez por área y por qué una ejecución sin páginas no tiene score.'
      }
    },
    titulo: {
      en: 'How the SEO score is calculated, and what it refuses to measure',
      pt: 'Como o score de SEO é calculado, e o que ele se recusa a medir',
      es: 'Cómo se calcula el score de SEO, y qué se niega a medir'
    },
    resumo: {
      en: 'The formula is five weights and one subtraction. The interesting part is the decision underneath it: a page counts once per area, no matter how many problems it has there.',
      pt: 'A fórmula são cinco pesos e uma subtração. O interessante é a decisão embaixo dela: uma página conta uma vez por área, não importa quantos problemas tenha ali.',
      es: 'La fórmula son cinco pesos y una resta. Lo interesante es la decisión que hay debajo: una página cuenta una vez por área, sin importar cuántos problemas tenga ahí.'
    },
    corpo: {
      en: [
        {
          tipo: 'p',
          texto:
            'Every completed crawl gets a number between 0 and 100. It appears on the report cover and on the chart that compares runs. This is what it is made of.'
        },
        { tipo: 'h2', texto: 'The formula' },
        {
          tipo: 'p',
          texto:
            "The score starts at 100 and subtracts, area by area, a share of that area's weight proportional to how many pages it affects:"
        },
        {
          tipo: 'lista',
          itens: ['Indexing — 25', 'Content — 30', 'Experience — 20', 'Links — 15', 'Media — 10']
        },
        {
          tipo: 'p',
          texto:
            'If a quarter of the pages have a content problem, content gives up a quarter of its 30 points. Add the five losses, subtract from 100, round. That is the whole calculation.'
        },
        { tipo: 'calculadora' },
        { tipo: 'h2', texto: 'A page counts once per area' },
        {
          tipo: 'p',
          texto:
            'This is the decision that shapes the number. A page missing a title, missing a description and missing an H1 has three content problems — and counts as one affected page.'
        },
        {
          tipo: 'p',
          texto:
            'Counting occurrences would make the score answer the wrong question. Three problems on the same page are one page to open and one deploy to ship; one problem on three pages is three. The score is meant to estimate the size of the job, not the length of the list.'
        },
        { tipo: 'h2', texto: 'What it refuses to do' },
        {
          tipo: 'p',
          texto:
            'A run with no pages gets no score at all — not zero. Zero is a measurement, and “we could not reach the site” is not a measurement. It shows as absent, and the run stays out of the comparison chart.'
        },
        {
          tipo: 'nota',
          texto:
            'The weights are a judgement, not a discovery. They say what we think costs more to leave broken — and they are the same for every site, which is exactly why the score is only worth reading against another run of the same site.'
        },
        { tipo: 'h2', texto: 'Comparing two runs' },
        {
          tipo: 'p',
          texto:
            'Two crawls of the same site rarely cover the same pages: each discovers addresses from the entry URL. A score that moved four points between different samples measured the sample. Use “Re-run” to crawl exactly the previous set — then the difference is about the site.'
        },
        {
          tipo: 'p',
          texto:
            'The report says which of the two situations produced the number you are looking at. It seemed dishonest to show the delta without that.'
        }
      ],
      pt: [
        {
          tipo: 'p',
          texto:
            'Toda execução concluída recebe um número entre 0 e 100. Ele aparece na capa do relatório e no gráfico que compara execuções. É disto que ele é feito.'
        },
        { tipo: 'h2', texto: 'A fórmula' },
        {
          tipo: 'p',
          texto:
            'O score parte de 100 e subtrai, área por área, uma parcela do peso daquela área proporcional a quantas páginas ela afeta:'
        },
        {
          tipo: 'lista',
          itens: ['Indexação — 25', 'Conteúdo — 30', 'Experiência — 20', 'Links — 15', 'Mídia — 10']
        },
        {
          tipo: 'p',
          texto:
            'Se um quarto das páginas tem problema de conteúdo, conteúdo abre mão de um quarto dos seus 30 pontos. Some as cinco perdas, subtraia de 100, arredonde. É o cálculo inteiro.'
        },
        { tipo: 'calculadora' },
        { tipo: 'h2', texto: 'Uma página conta uma vez por área' },
        {
          tipo: 'p',
          texto:
            'Esta é a decisão que dá forma ao número. Uma página sem título, sem descrição e sem H1 tem três problemas de conteúdo — e conta como uma página afetada.'
        },
        {
          tipo: 'p',
          texto:
            'Contar ocorrências faria o score responder à pergunta errada. Três problemas na mesma página são uma página para abrir e um deploy para publicar; um problema em três páginas são três. O score existe para estimar o tamanho do trabalho, não o comprimento da lista.'
        },
        { tipo: 'h2', texto: 'O que ele se recusa a fazer' },
        {
          tipo: 'p',
          texto:
            'Execução sem página nenhuma não recebe score — não recebe zero. Zero é uma medição, e “não conseguimos alcançar o site” não é medição. Aparece como ausente, e a execução fica fora do gráfico de comparação.'
        },
        {
          tipo: 'nota',
          texto:
            'Os pesos são um julgamento, não uma descoberta. Eles dizem o que achamos que custa mais caro deixar quebrado — e são os mesmos para qualquer site, que é justamente por que o score só vale lido contra outra execução do mesmo site.'
        },
        { tipo: 'h2', texto: 'Comparar duas execuções' },
        {
          tipo: 'p',
          texto:
            'Dois rastreamentos do mesmo site raramente cobrem as mesmas páginas: cada um descobre endereços a partir da URL inicial. Um score que subiu quatro pontos entre amostras diferentes mediu a amostra. Use “Repetir” para rastrear exatamente o conjunto anterior — aí a diferença é sobre o site.'
        },
        {
          tipo: 'p',
          texto:
            'O relatório diz qual das duas situações produziu o número que você está olhando. Mostrar a variação sem isso parecia desonesto.'
        }
      ],
      es: [
        {
          tipo: 'p',
          texto:
            'Toda ejecución completada recibe un número entre 0 y 100. Aparece en la portada del informe y en el gráfico que compara ejecuciones. De esto está hecho.'
        },
        { tipo: 'h2', texto: 'La fórmula' },
        {
          tipo: 'p',
          texto:
            'El score parte de 100 y resta, área por área, una parte del peso de esa área proporcional a cuántas páginas afecta:'
        },
        {
          tipo: 'lista',
          itens: [
            'Indexación — 25',
            'Contenido — 30',
            'Experiencia — 20',
            'Enlaces — 15',
            'Medios — 10'
          ]
        },
        {
          tipo: 'p',
          texto:
            'Si un cuarto de las páginas tiene un problema de contenido, contenido cede un cuarto de sus 30 puntos. Suma las cinco pérdidas, resta de 100, redondea. Ese es todo el cálculo.'
        },
        { tipo: 'calculadora' },
        { tipo: 'h2', texto: 'Una página cuenta una vez por área' },
        {
          tipo: 'p',
          texto:
            'Esta es la decisión que le da forma al número. Una página sin título, sin descripción y sin H1 tiene tres problemas de contenido — y cuenta como una página afectada.'
        },
        {
          tipo: 'p',
          texto:
            'Contar ocurrencias haría que el score respondiera la pregunta equivocada. Tres problemas en la misma página son una página que abrir y un despliegue que publicar; un problema en tres páginas son tres. El score existe para estimar el tamaño del trabajo, no la longitud de la lista.'
        },
        { tipo: 'h2', texto: 'Lo que se niega a hacer' },
        {
          tipo: 'p',
          texto:
            'Una ejecución sin ninguna página no recibe score — no recibe cero. Cero es una medición, y “no pudimos alcanzar el sitio” no lo es. Aparece como ausente, y la ejecución queda fuera del gráfico de comparación.'
        },
        {
          tipo: 'nota',
          texto:
            'Los pesos son un juicio, no un hallazgo. Dicen qué creemos que sale más caro dejar roto — y son los mismos para cualquier sitio, que es justamente por lo que el score solo vale leído contra otra ejecución del mismo sitio.'
        },
        { tipo: 'h2', texto: 'Comparar dos ejecuciones' },
        {
          tipo: 'p',
          texto:
            'Dos rastreos del mismo sitio rara vez cubren las mismas páginas: cada uno descubre direcciones a partir de la URL inicial. Un score que subió cuatro puntos entre muestras distintas midió la muestra. Usa “Repetir” para rastrear exactamente el conjunto anterior — así la diferencia es sobre el sitio.'
        },
        {
          tipo: 'p',
          texto:
            'El informe dice cuál de las dos situaciones produjo el número que estás mirando. Mostrar la variación sin eso parecía deshonesto.'
        }
      ]
    }
  }
];

/** Texto do post no idioma pedido, caindo no inglês como o resto do produto. */
export const noIdioma = (campo, idioma) =>
  campo == null ? null : (campo[idioma] ?? campo.en ?? null);

export const buscarPost = (slug) =>
  POSTS.find((p) => p.slug === slug || Object.values(p.slugs || {}).includes(slug)) || null;

export const slugNo = (post, idioma) => post.slugs?.[idioma] ?? post.slug;

/**
 * O caminho sem prefixo no idioma: o post troca de slug, as outras páginas
 * passam como vieram. Vai antes de `comPrefixo`, que não importa este arquivo
 * porque `scripts/gerarSitemap.js` só lê módulo sem import.
 */
export const traduzirCaminho = (caminho, idioma) => {
  const post = buscarPost(caminho.match(/^\/blog\/([^/]+)$/)?.[1]);
  return post ? `/blog/${slugNo(post, idioma)}` : caminho;
};

/** O título que cabe em duas linhas: o de busca quando o post tem, como no `<title>`. */
export const tituloCurto = (post, idioma) => noIdioma(post.seo?.titulo || post.titulo, idioma);
